"use client";

/**
 * Thin composition shell for the public `/products` page.
 *
 * Before task #16 this file was ~1,620 lines of card + filter + detail +
 * lightbox + fetch logic in one. Everything lives under
 * `components/products/**` + `hooks/useProductFilters.ts` +
 * `hooks/useProductsQuery.ts` now.
 *
 * Responsibilities that stay here:
 * - wire the zustand `useProductOverlay` store for deep-linkable open/close
 * - track the mobile vs desktop layout breakpoint (the product card/detail
 *   components render differently on each)
 * - coordinate the card-level lightbox (zoom icon) vs the detail-drawer's
 *   internal lightbox so Radix pointerdown-outside doesn't close the drawer
 *   when the user clicks the lightbox
 * - fetch `/api/categories` + `/api/colours` once to feed the filter lists
 */

import { useCallback, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import { toast } from "sonner";

import { useProductOverlay, type Product } from "@/stores/product-store";
import { useAddToCart } from "@/hooks/useAddToCart";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useProductFilters } from "@/hooks/useProductFilters";
import { useProductsQuery } from "@/hooks/useProductsQuery";
import { ProductsPageCart } from "@/components/cart/ProductsPageCart";

import { FilterSidebar } from "@/components/products/listing/FilterSidebar";
import type { CategoryTreeNode } from "@/components/products/FilterSections/CategorySection";
import {
  resolveVariantPrice,
  type VariantOption,
} from "@/components/products/VariantSelector";
import { ProductGrid } from "@/components/products/listing/ProductGrid";
import { ProductPagination } from "@/components/products/listing/ProductPagination";
import { ProductQuickNav } from "@/components/products/nav/ProductQuickNav";
import { ProductSortBar } from "@/components/products/listing/ProductSortBar";

/*
 * Interaction-gated overlays — code-split off the initial /products bundle via
 * next/dynamic({ ssr: false }). None render on first paint (the filter sheet is
 * closed, the detail drawer/lightbox open only on a tap), and none produce
 * SEO-visible HTML (the crawlable surface is the card <Link> → /p/[id]). This
 * keeps the busiest storefront route's initial JS + parse cost down. Pattern
 * mirrors components/providers/ClientProviders.tsx. ProductLightbox pulls in the shared
 * GalleryLightbox, so lazy-loading it keeps that out of the initial bundle too.
 */
const FilterSheet = dynamic(
  () => import("@/components/products/listing/FilterSheet").then((m) => ({ default: m.FilterSheet })),
  { ssr: false }
);
const ProductDetailDrawer = dynamic(
  () => import("@/components/products/detail/ProductDetailDrawer").then((m) => ({ default: m.ProductDetailDrawer })),
  { ssr: false }
);
const ProductLightbox = dynamic(
  () => import("@/components/products/detail/ProductLightbox").then((m) => ({ default: m.ProductLightbox })),
  { ssr: false }
);

interface ProductsClientProps {
  children?: React.ReactNode;
  /**
   * SSR seed for the category filter. Set by `/products/c/[slug]` landing
   * pages so the filter reflects the URL without a round-trip. URL query
   * state (`?categorySlug=...`) still wins if present.
   */
  initialCategorySlug?: string;
  /**
   * SSR-seeded category tree + flat colour list. The page already calls
   * `listCategories` server-side; passing the result through here avoids
   * a duplicate `/api/categories` round-trip on every collection visit
   * (~100ms before the filter sidebar populates).
   */
  initialCategoryTree?: CategoryTreeNode[];
  initialColours?: string[];
  /** Flower/plant names present on real products (`listFlowerFacets`). */
  initialFlowers?: string[];
  /**
   * SSR-seeded page of products (CRAWL-1). Passed for EVERY `/products`
   * request now — filtered or not, page 1 or page N — so the HTML always
   * carries real `/p/[slug]` card links. It used to be omitted for any
   * filtered URL, which shipped a skeleton (zero product anchors) to the 200+
   * internal links that point at filtered `/products?…` URLs.
   */
  initialProducts?: Product[];
  initialTotal?: number;
  initialPriceRangePence?: { min: number; max: number } | null;
  /** Page the server rendered (from `?page=`). */
  initialPage?: number;
  /**
   * Server-derived mobile hint (User-Agent based). Seeds the mobile-vs-desktop
   * layout so the grid renders the correct card shape on the first paint
   * instead of rendering the desktop grid and swapping after hydration (CLS).
   */
  initialIsMobile?: boolean;
}

/** Raw node shape returned by `GET /api/categories` (task #14). */
interface RawCategoryNode {
  id: string;
  name: string;
  slug: string;
  isActive?: number;
  children?: Array<{ id: string; name: string; slug: string; isActive?: number }>;
}

export function ProductsClient({
  children,
  initialCategorySlug,
  initialCategoryTree,
  initialColours,
  initialFlowers,
  initialProducts,
  initialTotal,
  initialPriceRangePence,
  initialPage = 1,
  initialIsMobile = false,
}: ProductsClientProps) {
  const searchParams = useSearchParams();
  // Cart write + GA4 add_to_cart in one call — see hooks/useAddToCart.
  const addToCart = useAddToCart();
  // Seed the layout from the server UA hint so SSR + first client render agree
  // on mobile-vs-desktop (no post-hydration grid swap → no CLS). The old
  // `mounted && isMobile` gate deferred the mobile layout to a post-mount
  // effect, which reflowed the grid on every phone visit. `useIsMobile` still
  // corrects the value from `matchMedia` after mount for any UA/viewport
  // mismatch.
  const useMobileLayout = useIsMobile(initialIsMobile);
  // SSR can paint product cards before their client click handlers attach.
  // This marker lets browser tests wait for the real interactive contract
  // rather than mistaking the server-rendered card shell for a ready drawer.
  const [isInteractive, setIsInteractive] = useState(false);
  useEffect(() => {
    const frame = requestAnimationFrame(() => setIsInteractive(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  // Product overlay state via zustand — decoupled from Next.js router so the
  // drawer's open/close doesn't cause filter re-renders.
  const selectedProduct = useProductOverlay((s) => s.selectedProduct);
  const overlayOpen = useProductOverlay((s) => s.overlayOpen);
  const openProduct = useProductOverlay((s) => s.openProduct);
  const closeOverlay = useProductOverlay((s) => s.closeOverlay);
  const resetOverlay = useProductOverlay((s) => s.resetOverlay);

  // Card-level lightbox (zoom icon) — separate from the detail drawer's own
  // lightbox so a card tap can skip straight to zoom without opening detail.
  const [lightboxProduct, setLightboxProduct] = useState<Product | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Filter + sort state (owns URL mirror for category + sort).
  const filters = useProductFilters({ initialCategorySlug });

  // Categories / colours — seeded from SSR when available, with the
  // hard-coded defaults as a final fallback so the filter is usable even
  // if both the SSR seed and the API are missing. The previous version
  // always made a redundant `/api/categories` + `/api/colours` round-trip
  // on every visit even though the server page had just loaded the same
  // data — ~100ms wasted before the filter sidebar populated.
  const [categoryTree, setCategoryTree] = useState<CategoryTreeNode[]>(
    initialCategoryTree ?? []
  );
  const [categories, setCategories] = useState<string[]>(() => {
    if (initialCategoryTree && initialCategoryTree.length > 0) {
      const names = new Set<string>();
      for (const p of initialCategoryTree) {
        names.add(p.name);
        for (const c of p.children ?? []) names.add(c.name);
      }
      return [...names];
    }
    return ["Fresh", "Preserved", "Crochet", "Accessories"];
  });
  const [colours, setColours] = useState<string[]>(
    initialColours && initialColours.length > 0
      ? initialColours
      : [
          "Pink",
          "Red",
          "White",
          "Cream",
          "Yellow",
          "Orange",
          "Purple",
          "Blue",
          "Green",
          "Neutral",
        ]
  );
  useEffect(() => {
    // Skip the categories fetch when SSR seeded us. Keep it as a recovery
    // path for surfaces that didn't pre-fetch (older callers).
    if (initialCategoryTree && initialCategoryTree.length > 0) return;
    fetch("/api/categories")
      .then(
        (r) =>
          (r.ok
            ? r.json()
            : Promise.resolve(null)) as Promise<{ categories?: RawCategoryNode[] } | null>
      )
      .then((data) => {
        if (!data?.categories?.length) return;
        const tree: CategoryTreeNode[] = data.categories.map((p) => ({
          id: p.id,
          name: p.name,
          slug: p.slug,
          children: (p.children ?? [])
            .filter((c) => (c.isActive ?? 1) !== 0)
            .map((c) => ({ id: c.id, name: c.name, slug: c.slug })),
        }));
        setCategoryTree(tree);
        const names = new Set<string>();
        for (const p of data.categories) {
          names.add(p.name);
          for (const c of p.children ?? []) names.add(c.name);
        }
        if (names.size > 0) setCategories([...names]);
      })
      .catch(() => {});
  }, [initialCategoryTree]);
  useEffect(() => {
    if (initialColours && initialColours.length > 0) return;
    fetch("/api/colours")
      .then((r) => (r.ok ? r.json() : Promise.resolve(null)) as Promise<{ colours?: string[] } | null>)
      .then((data) => {
        if (data?.colours?.length) setColours(data.colours);
      })
      .catch(() => {});
  }, [initialColours]);

  // Flower/plant-type facet. Sourced from REAL product data — there is no
  // invented fallback vocabulary, so an empty list simply hides the section.
  const [flowers, setFlowers] = useState<string[]>(initialFlowers ?? []);
  useEffect(() => {
    if (initialFlowers && initialFlowers.length > 0) return;
    fetch("/api/flowers")
      .then((r) => (r.ok ? r.json() : Promise.resolve(null)) as Promise<{ flowers?: string[] } | null>)
      .then((data) => {
        if (data?.flowers?.length) setFlowers(data.flowers);
      })
      .catch(() => {});
  }, [initialFlowers]);

  // Active nav-tab slug from `?tab=<slug>` (set by `<ProductQuickNav />`).
  // Not part of useProductFilters because it's a server-resolved predicate
  // rather than a user-picked facet — the rail is the canonical UI for it.
  // Gated to viewports where the rail is actually visible (`<md`). On
  // desktop the rail is `md:hidden`, so a tab toggled on mobile must NOT
  // narrow the desktop list — otherwise the user has no UI to clear it.
  const rawActiveTabSlug = searchParams.get("tab");
  const activeTabSlug = useMobileLayout ? rawActiveTabSlug : null;

  // Products + pagination + debounced price.
  const query = useProductsQuery({
    categoriesSelected: filters.categoriesSelected,
    occasionsSelected: filters.occasionsSelected,
    coloursSelected: filters.coloursSelected,
    flowersSelected: filters.flowersSelected,
    deliveryTiming: filters.deliveryTiming,
    minPrice: filters.minPrice,
    maxPrice: filters.maxPrice,
    sort: filters.sort,
    activeTabSlug,
    // Lock the listing to this collection when on `/products/<slug>`. The
    // sidebar's category section is hidden in that context — the user has
    // no UI to clear it, so making it a hard server-side filter prevents
    // any stale URL state or transient race from leaking products from
    // other collections into the grid.
    lockedCategorySlug: initialCategorySlug,
    // CRAWL-1: server-rendered page for THIS filter set + page number. Skips
    // the redundant mount fetch and gives crawlers real product links.
    initialProducts,
    initialTotal,
    initialPriceRangePence,
    initialPage,
  });

  // Open product from URL on initial page load (e.g. shared link
  // `/products?product=<slug>`). Matches by slug first, then id so older
  // `?product=<uuid>` links still re-open the overlay. Only fires once;
  // zustand owns subsequent opens.
  const productIdFromUrl = searchParams.get("product");
  const didOpenFromUrl = useRef(false);
  useEffect(() => {
    if (didOpenFromUrl.current) return;
    if (productIdFromUrl && query.products.length > 0) {
      const p = query.products.find(
        (x) => x.slug === productIdFromUrl || x.id === productIdFromUrl
      );
      if (p) {
        didOpenFromUrl.current = true;
        openProduct(p);
      }
    }
  }, [productIdFromUrl, query.products, openProduct]);

  // The overlay lives in a zustand store, and a zustand store is a MODULE
  // SINGLETON — it outlives this page's mount. Leaving /products with the
  // drawer open therefore left `overlayOpen: true` behind, and the next visit
  // (a clean /products with no `?product=`) re-opened the drawer on a product
  // the customer never tapped. Clear it on unmount so each mount starts fresh.
  // `resetOverlay` is used rather than `closeOverlay` because the latter also
  // rewrites the URL, which on unmount already belongs to the incoming page.
  useEffect(() => () => resetOverlay(), [resetOverlay]);

  const handleDialogOpenChange = useCallback(
    (open: boolean) => {
      if (!open) closeOverlay();
    },
    // closeOverlay is a stable zustand action.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handlePointerDownOutside = useCallback(
    (e: { target: EventTarget | null; preventDefault: () => void }) => {
      // Clicks inside the portal-rendered image lightbox must NOT dismiss the
      // product modal — the lightbox handles its own close. Guard by the click
      // target AND by the `data-lightbox-open` body flag (set while any lightbox
      // is open), so a slipped target check can't still close the modal.
      if (
        (e.target instanceof Element && e.target.closest("[data-product-lightbox]")) ||
        (typeof document !== "undefined" && document.body.hasAttribute("data-lightbox-open"))
      ) {
        e.preventDefault();
        return;
      }
      closeOverlay();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const handleAddToCart = useCallback(
    (p: Product, quantity = 1, variant?: VariantOption | null) => {
      // Price resolves from the chosen variant, else the base price. A product
      // priced purely via variants has a null base, so guard on the result.
      const price = variant
        ? resolveVariantPrice(variant, p.pricePence)
        : p.pricePence;
      if (price == null) return;
      addToCart({
        productId: p.id,
        quantity,
        priceSnapshot: price,
        title: p.title,
        imageKey: p.imageKey,
        imageUrlRaw: p.imageUrlRaw,
        category: p.category,
        isAddon: !!p.isAddon,
        ...(variant
          ? { variantId: variant.id, variantLabel: variant.label }
          : {}),
      });
      toast.success("Added to cart");
    },
    [addToCart]
  );

  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);

  const sidebarBodyProps = {
    categoryTree,
    categories,
    colours,
    flowers,
    sort: filters.sort,
    onSortChange: filters.setSort,
    categoriesSelected: filters.categoriesSelected,
    onToggleCategory: filters.toggleCategory,
    onReplaceCategorySelection: filters.setCategoriesSelected,
    deliveryTiming: filters.deliveryTiming,
    onDeliveryTimingChange: filters.setDeliveryTiming,
    occasionsSelected: filters.occasionsSelected,
    onToggleOccasion: filters.toggleOccasion,
    coloursSelected: filters.coloursSelected,
    onToggleColour: filters.toggleColour,
    flowersSelected: filters.flowersSelected,
    onToggleFlower: filters.toggleFlower,
    minPrice: filters.minPrice,
    maxPrice: filters.maxPrice,
    onPriceChange: filters.setPriceRange,
    priceRangePence: query.priceRangePence,
    openSections: filters.openSections,
    onToggleSection: filters.toggleSection,
    hasActiveFilters: filters.hasActiveFilters,
    onClearAllFilters: filters.clearAllFilters,
    // On a specific collection page (`/products/<slug>`) the user is
    // already inside one collection — don't let them accidentally switch
    // via a sidebar checkbox. The collection-first navigation model owns
    // that decision.
    hideCategoryFilter: Boolean(initialCategorySlug),
  };

  return (
    <div
      className="px-4 pt-4 pb-8 min-[400px]:px-6 md:px-10 md:pt-5 md:pb-12 lg:px-16"
      data-products-interactive={isInteractive ? "true" : undefined}
    >
      <div className="mx-auto max-w-7xl">
        {/*
         * Layout:
         *   - Mobile/tablet (< md): slim 56px sticky vertical rail
         *     (`<ProductQuickNav />`) on the left + product grid filling
         *     the rest of the row. The rail scrolls internally so any
         *     number of tabs fits. The rail-to-grid gap tightens on the
         *     smallest phones (`gap-2`) so the card content has room.
         *   - md+: the rail is `md:hidden`. On lg+, `<FilterSidebar />`
         *     takes over as the canonical desktop filter UI on the left.
         */}
        <div className="flex flex-row gap-2 min-[400px]:gap-3 md:gap-4 lg:gap-6">
          <ProductQuickNav
            controlsId="product-grid"
            scope={initialCategorySlug ?? null}
          />

          <div className="flex min-w-0 flex-1 flex-col gap-6 lg:flex-row lg:gap-8">
            <FilterSidebar {...sidebarBodyProps} />

            <div id="product-grid" className="min-w-0 flex-1">
              <ProductSortBar
                activeFilterCount={filters.activeFilterCount}
                onOpenFilters={() => setFiltersSheetOpen(true)}
              />

              <FilterSheet
                open={filtersSheetOpen}
                onOpenChange={setFiltersSheetOpen}
                {...sidebarBodyProps}
              />

              {children}

            <ProductGrid
              // STABLE surface id — deliberately not derived from the active
              // filters, or GA4's list report would fragment into hundreds of
              // one-off ids and "which surface drove the click" would be
              // unanswerable again.
              listId="products-grid"
              listName="Products grid"
              products={query.products}
              loading={query.loading}
              useMobileLayout={useMobileLayout}
              onOpenProduct={openProduct}
              onAddToCart={(p) => handleAddToCart(p)}
              onZoom={(p) => {
                setLightboxIndex(0);
                setLightboxProduct(p);
              }}
              onClearFilters={filters.clearAllFilters}
            />

              <ProductPagination
                page={query.page}
                total={query.total}
                limit={query.limit}
                onChange={query.setPage}
              />
            </div>
          </div>
        </div>
      </div>

      <ProductDetailDrawer
        product={selectedProduct}
        open={overlayOpen}
        onOpenChange={handleDialogOpenChange}
        onClose={closeOverlay}
        onPointerDownOutside={handlePointerDownOutside}
        onAddToCart={handleAddToCart}
        useMobileLayout={useMobileLayout}
      />
      <ProductsPageCart />

      <ProductLightbox
        product={lightboxProduct}
        index={lightboxIndex}
        onClose={() => {
          setLightboxProduct(null);
          setLightboxIndex(0);
        }}
        onPrev={() =>
          setLightboxIndex((i) => {
            // We don't know the total here — the Lightbox itself wraps
            // `GalleryLightbox` which wraps modulo arithmetic via onPrev/
            // onNext, so we handle wraparound below using the product's
            // own gallery length (parsed identically to ProductLightbox).
            const total = computeLightboxTotal(lightboxProduct);
            return total > 0 ? (i - 1 + total) % total : 0;
          })
        }
        onNext={() =>
          setLightboxIndex((i) => {
            const total = computeLightboxTotal(lightboxProduct);
            return total > 0 ? (i + 1) % total : 0;
          })
        }
      />
    </div>
  );
}

/**
 * Mirrors the gallery-count computation inside ProductLightbox so the shell
 * can handle prev/next wraparound without pulling the items array through
 * props. (Kept tiny and identical to the parser in ProductLightbox.)
 */
function computeLightboxTotal(product: Product | null): number {
  if (!product) return 0;
  let keys: string[] = [];
  try {
    if (product.galleryImageKeys) {
      const parsed = JSON.parse(product.galleryImageKeys) as unknown;
      keys = Array.isArray(parsed)
        ? parsed.filter((k): k is string => typeof k === "string")
        : [];
    }
  } catch {
    keys = [];
  }
  if (keys.length === 0) keys = [product.imageKey];
  return keys.length;
}
